import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";

type DisputeKind = "fraud" | "payout_missed" | "contract_breach";

export function DisputeNew() {
	const [params] = useSearchParams();
	const dealId = params.get("dealId") ?? "";
	const nav = useNavigate();
	const qc = useQueryClient();
	const [kind, setKind] = useState<DisputeKind>("contract_breach");
	const [notes, setNotes] = useState("");

	const mut = useMutation({
		mutationFn: () => api.post<{ id: string }>("/disputes", { dealId, kind, notes }),
		onSuccess: ({ id }) => {
			qc.invalidateQueries({ queryKey: ["disputes"] });
			qc.invalidateQueries({ queryKey: ["deals"] });
			nav(`/disputes/${id}`, { replace: true });
		},
	});

	return (
		<div className="space-y-4 p-6">
			<div>
				<Link to="/disputes" className="text-sm text-blue-700 hover:underline">
					← Disputes
				</Link>
			</div>
			<h1 className="text-xl font-semibold">Open dispute</h1>
			{!dealId && (
				<div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm">
					No <code>dealId</code> in URL. Open dispute from a deal page:{" "}
					<Link to="/deals" className="text-blue-700 underline">
						/deals
					</Link>
				</div>
			)}
			{dealId && (
				<div className="max-w-xl space-y-4">
					<div className="text-sm">
						<div className="text-xs text-neutral-500">Deal</div>
						<div className="font-mono text-neutral-900">{dealId}</div>
					</div>
					<label className="text-sm">
						Kind
						<select
							className="block w-full rounded border px-2 py-1"
							value={kind}
							onChange={(e) => setKind(e.target.value as DisputeKind)}
						>
							<option value="contract_breach">contract breach</option>
							<option value="fraud">fraud</option>
							<option value="payout_missed">payout missed</option>
						</select>
					</label>
					<label className="text-sm">
						Why are you raising this?
						<textarea
							rows={4}
							value={notes}
							onChange={(e) => setNotes(e.target.value)}
							className="block w-full rounded border px-2 py-1"
						/>
					</label>
					{mut.error && <div className="text-sm text-red-700">{(mut.error as Error).message}</div>}
					<div className="flex gap-2">
						<button
							type="button"
							disabled={!notes.trim() || mut.isPending}
							onClick={() => mut.mutate()}
							className="rounded bg-red-600 px-3 py-1 text-sm text-white hover:bg-red-700 disabled:opacity-50"
						>
							Submit dispute
						</button>
						<button
							type="button"
							onClick={() => nav(`/deals/${dealId}`)}
							className="rounded bg-neutral-200 px-3 py-1 text-sm hover:bg-neutral-300"
						>
							Cancel
						</button>
					</div>
				</div>
			)}
		</div>
	);
}
